import {
    AlphaAttribute,
    ColorAttribute,
    getAttributesToSerialize,
    NumberAttribute,
    StringOrNumberAttribute
} from '../../attributes';
import { Config } from '../../../video-data/config';
import { DynamicInferredHeight, DynamicInferredWidth } from '../../../enums';
import type { CanvasTextDynamicBoxBorderRendererSettings } from '../renderer/CanvasTextDynamicBoxBorder';
import { CanvasTextDynamicBoxBorderRenderer } from '../renderer/CanvasTextDynamicBoxBorder';
import { CanvasTextAssetDynamicGraphic } from './CanvasTextAssetDynamicGraphic';

/**
 * CanvasTextAssetDynamicBoxBorder is the Luminary equivalent of CanvasTextDynamicBoxBorder and provides a bridge
 * between Luminary and the CanvasText library.
 */
export class CanvasTextAssetDynamicBoxBorder extends CanvasTextAssetDynamicGraphic {
    override _classType = 'CanvasTextDynamicBox';

    override readonly type = Config.BOX_BORDER_GRAPHIC;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredWidth) })
    boxWidth = DynamicInferredWidth.width;
    @StringOrNumberAttribute({ choices: Object.values(DynamicInferredHeight) })
    boxHeight = DynamicInferredHeight.maxHeight;
    @NumberAttribute({ min: 0 })
    start0 = 0.25;
    @NumberAttribute({ min: 0 })
    end0 = 0.75;
    @NumberAttribute({ min: 0 })
    start1 = 0.75;
    @NumberAttribute({ min: 0 })
    end1 = 0.25;
    @NumberAttribute()
    horizontalPadding = 20;
    @NumberAttribute()
    verticalPadding = 20;
    @NumberAttribute({ min: 0 })
    borderThickness = 5;
    @ColorAttribute()
    borderColor = 0xFFFFFF;
    @AlphaAttribute()
    borderAlpha = 0.5;

    override getCanvasRendererSettings(): CanvasTextDynamicBoxBorderRendererSettings {
        const base = super.getCanvasRendererSettings();

        const boxWidth = typeof this.boxWidth === 'number' ? this.boxWidth * this.referenceToOutputMultiplier : this.boxWidth;
        const boxHeight = typeof this.boxHeight === 'number' ? this.boxHeight * this.referenceToOutputMultiplier : this.boxHeight;

        return {
            ...base,
            type: this.type,
            boxWidth,
            boxHeight,
            start0: this.start0,
            end0: this.end0,
            start1: this.start1,
            end1: this.end1,
            horizontalPadding: this.horizontalPadding * this.referenceToOutputMultiplier,
            verticalPadding: this.verticalPadding * this.referenceToOutputMultiplier,
            borderThickness: this.borderThickness * this.referenceToOutputMultiplier,
            borderColor: this.borderColor,
            borderAlpha: this.borderAlpha,
        };
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(getAttributesToSerialize(this));
    }

    /* The list of properties that can be animated during draw */
    override get animatableProperties() {
        // Since CanvasTextDynamicBoxBorder is responsible for rendering our text we need to ask it which properties it
        // supports. See its rendering logic for more details.
        return CanvasTextDynamicBoxBorderRenderer.animatableProperties;
    }

    /** @inheritDoc */
    override get animatableGeometricProperties() {
        return CanvasTextDynamicBoxBorderRenderer.animatableGeometricProperties;
    }

    override loadTextAssetStyle(settings) {
        super.loadTextAssetStyle(settings);

        if (settings.shape.boxWidth !== undefined) {
            this.boxWidth = settings.shape.boxWidth;
        }
        if (settings.shape.boxHeight !== undefined) {
            this.boxHeight = settings.shape.boxHeight;
        }
        if (settings.shape.start0 !== undefined) {
            this.start0 = settings.shape.start0;
        }
        if (settings.shape.end0 !== undefined) {
            this.end0 = settings.shape.end0;
        }
        if (settings.shape.start1 !== undefined) {
            this.start1 = settings.shape.start1;
        }
        if (settings.shape.end1 !== undefined) {
            this.end1 = settings.shape.end1;
        }
        if (settings.shape.horizontalPadding !== undefined) {
            this.horizontalPadding = settings.shape.horizontalPadding;
        }
        if (settings.shape.verticalPadding !== undefined) {
            this.verticalPadding = settings.shape.verticalPadding;
        }
        if (settings.shape.borderThickness !== undefined) {
            this.borderThickness = settings.shape.borderThickness;
        }
        if (settings.shape.borderColor !== undefined) {
            this.borderColor = settings.shape.borderColor;
        }
        if (settings.shape.borderAlpha !== undefined) {
            this.borderAlpha = settings.shape.borderAlpha;
        }
    }
}
